import React, { useState } from "react";
import api from "@/lib/api";
import { Loader2, Sparkles, X } from "lucide-react";

const FIELDS = [
  ["years_experience", "Years of experience", "e.g., 8 years"],
  ["current_position", "Current / most recent position", "e.g., Sales Manager at ABC Corp (2022–now)"],
  ["previous_positions", "Previous positions", "e.g., Sales Rep at XYZ (2018–2022)"],
  ["education", "Education", "e.g., B.A. Business, State University, 2018"],
  ["skills", "Skills", "e.g., negotiation, Salesforce, coaching"],
  ["certifications", "Certifications", "e.g., PMP, AWS SAA"],
  ["accomplishments", "Major accomplishments", "e.g., grew revenue 30%, led team of 12"],
  ["additional", "Additional details", "Anything else worth including"],
];

export const ResumeBuilderModal = ({ open, onClose, onUse }) => {
  const [form, setForm] = useState({ name: "", target_role: "" });
  const [freeform, setFreeform] = useState("");
  const [result, setResult] = useState("");
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [err, setErr] = useState("");

  if (!open) return null;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const generate = async () => {
    setErr("");
    if (!form.name?.trim() || !form.target_role?.trim()) {
      setErr("Name and target role are required.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/resume/builder/generate", { ...form, freeform });
      setResult(data.resume_text || "");
      setEditing(false);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Generation failed. Please try again.");
    } finally { setBusy(false); }
  };

  const useResume = async () => {
    setSaving(true);
    setErr("");
    try {
      const { data } = await api.post("/resume/save-text", {
        name: `${form.name.trim()} · AI Resume (${form.target_role.trim()})`,
        content: result,
      });
      onUse?.(data);
      onClose?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not save the resume.");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="resume-builder-modal">
      <div className="w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-2xl border border-zinc-800 bg-zinc-950 p-6 shadow-2xl">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-amber-400 mb-1">Create My Resume with AI</div>
            <h3 className="text-lg font-semibold text-zinc-50">Tell us about yourself. AI does the writing.</h3>
            <p className="text-xs text-zinc-500 mt-1">We only use facts you provide. Nothing is ever invented.</p>
          </div>
          <button onClick={onClose} data-testid="builder-close-btn" className="text-zinc-500 hover:text-zinc-200"><X className="h-4 w-4" /></button>
        </div>

        {!result ? (
          <div className="mt-5 space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <input data-testid="builder-name" value={form.name || ""} onChange={set("name")} placeholder="Your name *"
                className="bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2 text-sm text-zinc-50 placeholder:text-zinc-600 focus:border-amber-500/50 focus:outline-none" />
              <input data-testid="builder-role" value={form.target_role || ""} onChange={set("target_role")} placeholder="Target job / position *"
                className="bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2 text-sm text-zinc-50 placeholder:text-zinc-600 focus:border-amber-500/50 focus:outline-none" />
              {FIELDS.map(([k, label, ph]) => (
                <input key={k} data-testid={`builder-${k}`} value={form[k] || ""} onChange={set(k)} placeholder={`${label} (optional)`} title={ph}
                  className="bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2 text-sm text-zinc-100 placeholder:text-zinc-600 focus:border-amber-500/50 focus:outline-none" />
              ))}
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5">Or just tell us about your experience</div>
              <textarea data-testid="builder-freeform" value={freeform} onChange={(e) => setFreeform(e.target.value)} rows={4}
                placeholder={"In your own words… e.g., \"I've worked in sales for 8 years. For the last 4 years I was a sales manager at ABC Corp, managing 12 people and increasing revenue by about 30%…\""}
                className="w-full bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2 text-sm text-zinc-100 placeholder:text-zinc-600 focus:border-amber-500/50 focus:outline-none leading-relaxed" />
            </div>
            {err && <div className="text-xs text-rose-300">{String(err)}</div>}
            <button onClick={generate} disabled={busy} data-testid="builder-generate-btn"
              className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-amber-950 font-semibold rounded-lg px-4 py-2.5 text-sm disabled:opacity-60">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              {busy ? "Writing your resume…" : "Generate my resume"}
            </button>
          </div>
        ) : (
          <div className="mt-5 space-y-3">
            {editing ? (
              <textarea data-testid="builder-edit-area" value={result} onChange={(e) => setResult(e.target.value)} rows={18}
                className="w-full bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2 text-xs text-zinc-100 font-mono leading-relaxed focus:border-amber-500/50 focus:outline-none" />
            ) : (
              <pre data-testid="builder-preview" className="whitespace-pre-wrap bg-zinc-900 border border-zinc-800 rounded-lg p-4 text-xs text-zinc-200 font-mono leading-relaxed max-h-96 overflow-y-auto">{result}</pre>
            )}
            {err && <div className="text-xs text-rose-300">{String(err)}</div>}
            <div className="flex flex-wrap gap-2">
              <button onClick={useResume} disabled={saving} data-testid="builder-use-btn"
                className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-amber-950 font-semibold rounded-lg px-4 py-2 text-sm disabled:opacity-60">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Use This Resume
              </button>
              <button onClick={() => setEditing((v) => !v)} data-testid="builder-edit-btn"
                className="border border-zinc-700 hover:border-zinc-500 text-zinc-200 rounded-lg px-4 py-2 text-sm">
                {editing ? "Done editing" : "Edit"}
              </button>
              <button onClick={generate} disabled={busy} data-testid="builder-regenerate-btn"
                className="border border-zinc-700 hover:border-zinc-500 text-zinc-200 rounded-lg px-4 py-2 text-sm disabled:opacity-60">
                {busy ? "Regenerating…" : "Regenerate"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
